import React from 'react';
import { useLang } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Check, Zap, Star, ArrowUpRight } from 'lucide-react';

const PACKAGE_LINKS = {
  basic: 'https://www.host-on.de/zimbra/konfigurieren?pkg=082b87ab-3100-48d4-8a62-10e3bb15e8ba&slug=zimbra-basic&name=Zimbra%20Basic&price=1.69',
  plus: 'https://www.host-on.de/zimbra/konfigurieren?pkg=c5cfa146-83bd-4e72-b1c0-8d41ba84c039&slug=zimbra-plus&name=Zimbra%20Plus&price=9.98',
  pro: 'https://www.host-on.de/zimbra/konfigurieren?pkg=39848921-fec5-4284-8c00-721140df3e17&slug=zimbra-pro&name=Zimbra%20Pro&price=24.99',
};

export default function PricingSection() {
  const { t } = useLang();

  const packages = [
    { key: 'basic', highlight: false, glow: false },
    { key: 'plus', highlight: true, glow: false },
    { key: 'pro', highlight: false, glow: true },
  ];

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 lg:py-32 bg-[#050505] relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-neon/[0.03] rounded-full blur-[200px]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 lg:mb-20"
        >
          <p className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-neon mb-4 bg-neon/10 border border-neon/20 rounded-full px-4 py-1.5 mx-auto">
            <Zap className="w-3.5 h-3.5" />
            {t('pricing.overline')}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tight text-white mb-5">
            {t('pricing.title')}
          </h2>
          <p className="text-base sm:text-lg text-zinc-400 font-light max-w-2xl mx-auto">
            {t('pricing.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-5 items-stretch">
          {packages.map(({ key, highlight, glow }, i) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              className={`relative rounded-3xl transition-all duration-500 group ${
                highlight ? 'md:-mt-4 md:-mb-4' : ''
              }`}
              data-testid={`pricing-card-${key}`}
            >
              {/* Animated gradient border for highlighted card */}
              {highlight && (
                <div className="absolute inset-0 rounded-3xl gradient-border" />
              )}
              {glow && (
                <div className="absolute inset-0 rounded-3xl border-2 border-pink/50 shadow-[0_0_60px_rgba(255,0,127,0.15)]" />
              )}
              
              <div className={`relative h-full rounded-3xl p-8 lg:p-10 ${
                highlight
                  ? 'bg-[#0a0a0f] z-10'
                  : glow
                  ? 'bg-surface'
                  : 'bg-surface border border-white/[0.06]'
              }`}>
                {highlight && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                    <div className="flex items-center gap-1.5 bg-neon text-black text-xs font-black uppercase tracking-wider px-5 py-2 rounded-full shadow-[0_0_20px_rgba(212,255,0,0.4)]">
                      <Star className="w-3.5 h-3.5" fill="currentColor" />
                      {t('pricing.recommended')}
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <img src="/images/zimbra-logo.svg" alt="Zimbra" className="w-10 h-10 mb-3" />
                  <h3 className="font-heading text-xl lg:text-2xl font-bold text-white mb-1">
                    {t(`pricing.${key}.name`)}
                  </h3>
                  <p className="text-sm text-zinc-600">{t(`pricing.${key}.desc`)}</p>
                </div>

                <div className="flex items-baseline gap-1.5 mb-10">
                  <span className={`font-heading text-5xl lg:text-7xl font-black ${
                    highlight ? 'text-gradient-animated' : glow ? 'text-pink' : 'text-white'
                  }`}>
                    {t(`pricing.${key}.price`)}
                  </span>
                  <div className="flex flex-col">
                    <span className="text-sm text-zinc-600 font-medium">EUR</span>
                    <span className="text-xs text-zinc-700">{t('pricing.monthly')}</span>
                  </div>
                </div>

                <ul className="space-y-3.5 mb-10">
                  {t(`pricing.${key}.features`)?.map((feat, fi) => (
                    <li key={fi} className="flex items-center gap-3 text-sm text-zinc-300 group/item">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        highlight ? 'bg-neon/20' : glow ? 'bg-pink/20' : 'bg-white/5'
                      }`}>
                        <Check className={`w-3 h-3 ${
                          highlight ? 'text-neon' : glow ? 'text-pink' : 'text-zinc-500'
                        }`} />
                      </div>
                      <span className="group-hover/item:text-white transition-colors">{feat}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={PACKAGE_LINKS[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group/btn flex items-center justify-center gap-2 w-full font-bold text-sm uppercase tracking-wide rounded-full px-6 py-4 transition-all duration-300 ${
                    highlight
                      ? 'bg-neon text-black hover:bg-white hover:shadow-[0_0_30px_rgba(212,255,0,0.5)] glow-neon'
                      : glow
                      ? 'bg-pink text-white hover:shadow-[0_0_40px_rgba(255,0,127,0.5)] glow-pink'
                      : 'bg-white/[0.04] text-white border border-white/10 hover:bg-white/[0.08] hover:border-white/20'
                  }`}
                  data-testid={`cta-pricing-${key}`}
                >
                  {t('pricing.cta')}
                  <ArrowUpRight className="w-4 h-4 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
